import { useRef } from 'react';
import type { TransactionFieldPromotion } from '@capture/shared';
import { useFormConfigStore } from '../state/store';
import { useRemittanceIssues } from './useRemittanceIssues';
import './TransactionFieldsSection.css';

/**
 * SP6 Task 7 — Transaction Fields section of the Remittance job-config tab.
 * Renders `remittance.transactionFields`: promote-from-attachment mappings
 * that expose a form field's value as `transaction.<name>` (consumed by
 * ExpressionEditor pickers elsewhere via `buildPickerData`, and at runtime by
 * `materializeTransactionFields`). Every write recomputes and sends the FULL
 * array via `updateRemittance({ transactionFields: next })` — the store's
 * merge is shallow, so a partial patch would silently drop other rows.
 *
 * `TransactionFieldPromotion` carries no id of its own (unlike
 * TransactionRuleConfig), so rows use the same synthetic ref-array row-id
 * mechanism as BalancePolicySection's rule rows: a lazily-grown/trimmed
 * parallel id array that reorders/splices in lockstep with the promotions
 * array, mutated synchronously in the same handlers that write the store.
 * Never persisted — the stored `TransactionFieldPromotion` shape is
 * unchanged. These rows don't host an ExpressionEditor (no uncommitted local
 * editor state to protect), but the same mechanism keeps remove-at-index
 * from re-keying a row a user is mid-edit on.
 */
export function TransactionFieldsSection() {
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const updateRemittance = useFormConfigStore((s) => s.updateRemittance);
  const issues = useRemittanceIssues();

  const promotions = contentV3?.remittance?.transactionFields ?? [];
  const forms = contentV3?.forms ?? [];

  const rowIdCounter = useRef(0);
  const rowIdsRef = useRef<string[]>([]);

  function rowIds(count: number): string[] {
    const ids = rowIdsRef.current;
    while (ids.length < count) ids.push(`txnfield-row-${rowIdCounter.current++}`);
    if (ids.length > count) ids.length = count;
    return ids;
  }

  function writePromotions(next: TransactionFieldPromotion[]) {
    updateRemittance({ transactionFields: next });
  }

  function handleAdd() {
    writePromotions([...promotions, { name: '', promoteFrom: { classificationKey: '', field: '' } }]);
  }

  function handleRemove(index: number) {
    rowIdsRef.current.splice(index, 1);
    writePromotions(promotions.filter((_, i) => i !== index));
  }

  function handleNameChange(index: number, name: string) {
    writePromotions(promotions.map((p, i) => (i === index ? { ...p, name } : p)));
  }

  function handleFormChange(index: number, classificationKey: string) {
    writePromotions(
      promotions.map((p, i) => (i === index ? { ...p, promoteFrom: { classificationKey, field: '' } } : p)),
    );
  }

  function handleFieldChange(index: number, field: string) {
    writePromotions(
      promotions.map((p, i) => (i === index ? { ...p, promoteFrom: { ...p.promoteFrom, field } } : p)),
    );
  }

  const ids = rowIds(promotions.length);

  return (
    <section className="remittance-transaction-fields">
      <h3 className="remittance-transaction-fields__heading">Transaction fields</h3>

      {issues.fields.length > 0 && (
        <ul className="remittance-transaction-fields__issues" role="alert">
          {issues.fields.map((issue, i) => (
            <li key={`${issue.code}-${i}`}>{issue.message}</li>
          ))}
        </ul>
      )}

      <ol className="remittance-transaction-fields__rows">
        {promotions.map((promotion, index) => {
          const selectedForm = forms.find((f) => f.classificationKey === promotion.promoteFrom.classificationKey);
          const fieldOptions = selectedForm?.fields ?? [];
          return (
            <li key={ids[index]} className="remittance-transaction-fields__row">
              <label className="remittance-transaction-fields__field">
                {`Transaction field ${index + 1} name`}
                <input
                  aria-label={`Transaction field ${index + 1} name`}
                  value={promotion.name}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                />
              </label>

              <label className="remittance-transaction-fields__field">
                {`Transaction field ${index + 1} source form`}
                <select
                  aria-label={`Transaction field ${index + 1} source form`}
                  value={promotion.promoteFrom.classificationKey}
                  onChange={(e) => handleFormChange(index, e.target.value)}
                >
                  <option value="" disabled>
                    Select a form
                  </option>
                  {forms.map((form) => (
                    <option key={form.id} value={form.classificationKey}>
                      {form.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="remittance-transaction-fields__field">
                {`Transaction field ${index + 1} source field`}
                <select
                  aria-label={`Transaction field ${index + 1} source field`}
                  value={promotion.promoteFrom.field}
                  onChange={(e) => handleFieldChange(index, e.target.value)}
                  disabled={!selectedForm}
                >
                  <option value="" disabled>
                    Select a field
                  </option>
                  {fieldOptions.map((field) => {
                    const internalName = field.internalName ?? field.name;
                    return (
                      <option key={field.fieldDefinitionId} value={internalName}>
                        {internalName}
                      </option>
                    );
                  })}
                </select>
              </label>

              <button
                type="button"
                className="remittance-transaction-fields__remove"
                onClick={() => handleRemove(index)}
                aria-label={`Remove transaction field ${index + 1}`}
              >
                Remove
              </button>
            </li>
          );
        })}
      </ol>

      <button type="button" className="remittance-transaction-fields__add" onClick={handleAdd}>
        Add transaction field
      </button>
    </section>
  );
}
